import { auth } from "../../../core/firebase/firebase";
import {
  bootstrapRemoteSession,
  getRemoteCurrentUser,
  getRemoteProfile,
  updateRemoteCurrentUser,
} from "./profile.api";
import { DEFAULT_PROFILE_COVER_THEME_ID } from "../profileCoverThemes";
import { sanitizeDeepStringValues } from "../../../utils/textSecurity";
import { mergeUserSettings } from "../../settings/settings.defaults";

const PROFILE_POLL_INTERVAL_MS = 60000;
const remoteProfileCacheByUserId = new Map();
const remoteProfileListenersByUserId = new Map();

function hasOwnField(target, key) {
  return (
    target &&
    Object.getPrototypeOf(target) === Object.prototype &&
    Object.prototype.hasOwnProperty.call(target, key)
  );
}

function resolvePrimaryProfilePhotoUrl(profileData) {
  const primaryPhoto = Array.isArray(profileData?.media?.photos)
    ? profileData.media.photos[0]
    : null;

  return primaryPhoto?.thumbnailUrl || primaryPhoto?.url || profileData?.photoURL || null;
}

function resolveProfileCoverThemeId(profileData) {
  return (
    profileData?.profile?.headerCoverId ||
    profileData?.profile?.coverThemeId ||
    DEFAULT_PROFILE_COVER_THEME_ID
  );
}

function normalizeStoredProfile(profileData) {
  if (!profileData) {
    return null;
  }

  const resolvedCoverThemeId = resolveProfileCoverThemeId(profileData);
  const resolvedPhotoURL = resolvePrimaryProfilePhotoUrl(profileData);
  const normalizedPhotos = Array.isArray(profileData.media?.photos)
    ? profileData.media.photos
    : [];
  const resolvedProfileType =
    profileData.profile?.profileType || profileData.profileType || "";

  return {
    ...profileData,
    settings: mergeUserSettings(profileData.settings),
    photoURL: resolvedPhotoURL,
    media: {
      ...(profileData.media || {}),
      photos: normalizedPhotos,
      coverPhoto: profileData.media?.coverPhoto || normalizedPhotos[0]?.url || null,
    },
    profile: {
      ...(profileData.profile || {}),
      profileType: resolvedProfileType,
      identityLockedAt: profileData.profile?.identityLockedAt || null,
      coverThemeId: profileData.profile?.coverThemeId || resolvedCoverThemeId,
      headerCoverId: resolvedCoverThemeId,
    },
  };
}

function getProfileListeners(userId) {
  if (!remoteProfileListenersByUserId.has(userId)) {
    remoteProfileListenersByUserId.set(userId, new Set());
  }

  return remoteProfileListenersByUserId.get(userId);
}

function subscribeToRemoteProfileChannel(userId, callback) {
  const listeners = getProfileListeners(userId);
  listeners.add(callback);

  return () => {
    const currentListeners = remoteProfileListenersByUserId.get(userId);

    if (!currentListeners) {
      return;
    }

    currentListeners.delete(callback);

    if (currentListeners.size === 0) {
      remoteProfileListenersByUserId.delete(userId);
    }
  };
}

function publishRemoteProfile(userId, remoteProfile) {
  if (!userId || !remoteProfile) {
    return;
  }

  remoteProfileCacheByUserId.set(userId, remoteProfile);

  const listeners = remoteProfileListenersByUserId.get(userId);

  if (!listeners || listeners.size === 0) {
    return;
  }

  listeners.forEach((listener) => {
    listener(remoteProfile);
  });
}

async function fetchRemoteProfile(userId) {
  if (auth.currentUser?.uid && auth.currentUser.uid === userId) {
    return getRemoteCurrentUser();
  }

  return getRemoteProfile(userId);
}

export async function getUserProfile(userId) {
  if (!userId) {
    return null;
  }

  const remoteProfile = await fetchRemoteProfile(userId);

  if (remoteProfile) {
    publishRemoteProfile(userId, remoteProfile);
  }

  return normalizeStoredProfile(remoteProfile);
}

export function subscribeUserProfile(userId, onChange, onError) {
  if (!userId) {
    return () => {};
  }

  let isCancelled = false;
  let isInitialized = false;

  const handleRemoteUpdate = (remoteProfile) => {
    if (isCancelled) {
      return;
    }
    isInitialized = true;
    onChange(normalizeStoredProfile(remoteProfile));
  };
  const unsubscribeRemoteChannel = subscribeToRemoteProfileChannel(
    userId,
    handleRemoteUpdate
  );
  const cachedProfile = remoteProfileCacheByUserId.get(userId);

  if (cachedProfile) {
    isInitialized = true;
    onChange(normalizeStoredProfile(cachedProfile));
  }

  const pullRemoteProfile = async () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }

    try {
      const nextRemoteProfile = await fetchRemoteProfile(userId);

      if (isCancelled) {
        return;
      }

      if (!nextRemoteProfile) {
        if (!isInitialized) {
          onChange(null);
        }
        return;
      }

      publishRemoteProfile(userId, nextRemoteProfile);
    } catch (error) {
      if (!isCancelled && !isInitialized) {
        onError?.(error);
      }
    }
  };

  pullRemoteProfile();

  let refreshIntervalId = null;
  let visibilityChangeHandler = null;

  if (typeof window !== "undefined") {
    refreshIntervalId = window.setInterval(pullRemoteProfile, PROFILE_POLL_INTERVAL_MS);

    visibilityChangeHandler = () => {
      if (!isCancelled && document.visibilityState === "visible") {
        pullRemoteProfile();
      }
    };
    document.addEventListener("visibilitychange", visibilityChangeHandler);
  }

  return () => {
    isCancelled = true;
    unsubscribeRemoteChannel();

    if (refreshIntervalId !== null) {
      window.clearInterval(refreshIntervalId);
    }

    if (visibilityChangeHandler !== null) {
      document.removeEventListener("visibilitychange", visibilityChangeHandler);
    }
  };
}

export async function createUserProfile(firebaseUser) {
  if (!firebaseUser?.uid) {
    return;
  }

  const result = await bootstrapRemoteSession(firebaseUser);

  if (result?.user) {
    publishRemoteProfile(firebaseUser.uid, result.user);
  }
}

// requestOptions : { firebaseUser, sessionGuard } pour une operation liee a une
// session precise (transport : jamais d'envoi ni de reponse pour une autre).
export async function updateUserProfile(userId, data, requestOptions = {}) {
  if (!userId) {
    throw new Error("userId requis");
  }

  const sanitizedData = sanitizeDeepStringValues(data);

  const updatedRemoteProfile = await updateRemoteCurrentUser(sanitizedData, requestOptions);

  if (updatedRemoteProfile) {
    publishRemoteProfile(userId, updatedRemoteProfile);
  }

  return {
    success: true,
    profile: normalizeStoredProfile(updatedRemoteProfile),
  };
}

export async function updateUserAvatar(userId, photoURL) {
  return updateUserProfile(userId, { photoURL });
}

export async function updateUserLocation(userId, location) {
  if (location === null) {
    return updateUserProfile(userId, { location: null });
  }

  const normalizedLocation =
    hasOwnField(location, "coordinates") ||
    hasOwnField(location, "lat") ||
    hasOwnField(location, "lng") ||
    hasOwnField(location, "city") ||
    hasOwnField(location, "address") ||
    hasOwnField(location, "label") ||
    hasOwnField(location, "country")
      ? location
      : null;

  return updateUserProfile(userId, { location: normalizedLocation });
}

